"use client";
import { useAuthStore } from "@core/store/storeAuth";
import { useStoreForm } from "@core/store/storeForm";
import { IBill, IFoodOptionFromOrder } from "@core/types/requests/bill";
import { IDeliveryAddress } from "@core/types/storeForm";
import { fetcherAlegriia, requestAlegriia } from "@utils/requests";
import axios, { AxiosError } from "axios";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";
import useSWR from "swr";
import { useShallow } from "zustand/react/shallow";

export interface IFoodBill {
  idFood: number;
  quantity: number;
  idFoodVariant?: number | undefined;
  foodOptions?: IFoodOptionFromOrder[] | undefined;
  description: string | null | undefined;
  stations: null | number[];
}

export const useBill = () => {
  const { push } = useRouter();
  const { lang = "es" } = useParams<{ lang: string }>();

  const { token } = useAuthStore(
    useShallow((state) => ({
      token: state.token,
    })),
  );

  const { replaceOldOrders, setTableNumber, setIdAddress, tableNumber } =
    useStoreForm(
      useShallow((state) => ({
        replaceOldOrders: state.replaceOldOrders,
        setTableNumber: state.setTableNumber,
        setIdAddress: state.setIdAddress,
        tableNumber: state.tableNumber,
      })),
    );

  const { data, mutate, isLoading } = useSWR<IBill>(
    token && { url: "/bill", auth: token },
    fetcherAlegriia,
    {
      // Poll while an online payment is pending; refetch when returning from the gateway
      refreshInterval: (latest) =>
        latest?.pendingPayments?.length ? 10000 : 0,
      revalidateOnFocus: true,
    },
  );

  useEffect(() => {
    if (!data) return;
    if (data.orders.length)
      replaceOldOrders?.(
        data.orders.map(
          ({
            idOrder,
            idUser,
            idPay,
            idPaymentAttempt,
            food,
            foodVariant,
            quantity,
            description,
            foodOptions,
            orderState,
          }) => ({
            idOrder: idOrder?.toString(),
            idUser,
            idPay,
            idPaymentAttempt,
            food,
            quantity,
            description,
            idAddress: data.idAddress,
            foodVariant,
            foodOptions: foodOptions?.map((i) => i),
            orderState,
            stations: null,
            categoryName: food.subFoodCategory?.foodCategory?.category ?? "",
          }),
        ),
      );
    if (!!data.idAddress) setIdAddress?.(data.idAddress);
  }, [data, replaceOldOrders, setIdAddress]);

  useEffect(() => {
    // Update tableNumber
    if (!data) return;
    if (
      !!data.seat?.seatNumber &&
      (`${data.seat.seatNumber}` !== `${tableNumber}` || !tableNumber)
    )
      setTableNumber?.(data.seat?.idSeat?.toString());
  }, [tableNumber, setTableNumber, data]);

  const createBill = useCallback(
    async ({
      foods,
      idAddress,
      idSeat,
      deliveryAddress,
      pickupDate,
      createdByRestaurant = false,
      shouldPayFirst,
      waitingConfirmation,
      payWithPaymentGateway,
    }: {
      idAddress: number;
      foods: IFoodBill[];
      idSeat?: string;
      deliveryAddress?: IDeliveryAddress;
      pickupDate?: Date;
      createdByRestaurant?: boolean;
      shouldPayFirst?: boolean;
      waitingConfirmation?: boolean;
      payWithPaymentGateway?: boolean;
    }): Promise<number | undefined> => {
      try {
        const res = await requestAlegriia.post<{ idBill: number }>(
          "/bill",
          {
            idAddress,
            idSeat,
            foods,
            ...(!!deliveryAddress
              ? {
                  deliveryAddress: {
                    idCity: deliveryAddress?.city,
                    ...deliveryAddress,
                  },
                }
              : {}),
            pickupDate,
            createdByRestaurant,
            shouldPayFirst,
            waitingConfirmation,
            payWithPaymentGateway,
          },
          {
            headers: { Authorization: token },
          },
        );
        if (res) return res.data.idBill;
      } catch (err) {
        const error = err as Error | AxiosError;
        if (axios.isAxiosError(error) && error.status === 401) {
          push(`/${lang}/login`);
        }
        throw error;
      }
    },
    [lang, push, token],
  );

  const leaveTable = useCallback(
    async (idBill: string) => {
      await requestAlegriia.patch(
        "/bill/leave-seat",
        { idBill },
        {
          headers: { Authorization: token },
        },
      );
    },
    [token],
  );

  const updateBill = useCallback(
    async ({
      idBill,
      waitingConfirmation,
      foods,
      createdByRestaurant = false,
    }: {
      idBill: number;
      foods: IFoodBill[];
      waitingConfirmation?: boolean;
      // Only the restaurant panel (tables) sends true: those orders get no customer
      createdByRestaurant?: boolean;
    }) => {
      try {
        await requestAlegriia.patch(
          "/bill",
          {
            idBill,
            foods,
            waitingConfirmation,
            createdByRestaurant,
          },
          {
            headers: { Authorization: token },
          },
        );
      } catch (err) {
        const error = err as Error | AxiosError;
        if (axios.isAxiosError(error) && error.status === 401)
          return push(`/${lang}/login`);
        throw error;
      }
    },
    [lang, push, token],
  );

  return useMemo(
    () => ({
      // data: !!data?.idDeliveryAddress || !!data?.pickupDate ? undefined : data,
      data,
      isLoading,
      mutate,
      createBill,
      updateBill,
      leaveTable,
    }),
    [createBill, data, isLoading, leaveTable, mutate, updateBill],
  );
};
